/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

// Use https://github.com/eligrey/FileSaver.js to save files
// locally. FileSaver is used as it has code to manage quirks
// among browsers and operating systems i.e. more robust
// than rolling out one on our own
import { saveAs } from "file-saver";
import { jwtDecode } from "jwt-decode";
import { JwtPayload as BaseJwtPayload } from "jwt-decode";

interface JwtPayload extends BaseJwtPayload {
  oid?: string;
}
import { doPostProcessing } from "./postprocessing";
import { updateFormatting } from "./postprocessing";
import { hasFormattingChanged } from "./postprocessing";

enum FailureReasons {
  Success = 0,
  GenericFailure = 1,
  HTTPFailed = 2,
}

/* global document, Office, Word */

const StartRecordingButtonName = "start-recording";
const StopRecordingButtonName = "stop-recording";
const UploadRecordingButtonName = "upload-recording";
const DisabledAttributeName = "disabled";

const paymentlink = "https://www.saras-ai.com/payments";
let mediaRecorder: MediaRecorder;
let chunks = [];
let recording: File;
let transcription: string;
let failureReason: FailureReasons = FailureReasons.Success;
let usedMinutes: number;
let creditsMinutes: number;
let mimeToExtensionMap = new Map<string, string>();
let userTokenEncoded = null;
let userTokenError = null;
let tokenExpiryTime = null;
let requireIDTokenFeature = true;

const maxaudiosizebytes = 25 * 1024 * 1024;
const thresholdForAvailableMinutesWarning = 5;

/*
 * The order in which mime types are listed, is important
 * 1. It is ordered so that supported mime types
 *   are listed up front
 * 2. Apple supports mp4, mp4/aac and mp4/mp4a. Plain mp4
 *   is also supported. It is critical that
 *   plain mp4 is listed ahead of the remaining mime types
 *   supported by Apple. Otherwise, transcription will
 *   initially fail and then we'll attempt to convert
 *   to mp3 using ffmpeg and attempt transcription again
 */
mimeToExtensionMap.set("audio/webm; codecs=opus", ".webm");
mimeToExtensionMap.set("audio/webm;codecs=opus", ".webm");
mimeToExtensionMap.set("audio/webm", ".webm");
mimeToExtensionMap.set("audio/ogg; codecs=vorbis", ".ogg");
mimeToExtensionMap.set("audio/ogg;codecs=vorbis", ".ogg");
mimeToExtensionMap.set("audio/ogg", ".ogg");
mimeToExtensionMap.set("audio/mp3", ".mp3");
mimeToExtensionMap.set("audio/mp4", ".mp4");
mimeToExtensionMap.set("audio/mp4; codecs=aac", ".mp4");
mimeToExtensionMap.set("audio/mp4;codecs=aac", ".mp4");
mimeToExtensionMap.set("audio/mp4; codecs=mp4a", ".mp4");
mimeToExtensionMap.set("audio/mp4;codecs=mp4a", ".mp4");

Office.onReady(async (info) => {
  if (info.host === Office.HostType.Word) {
    // Setup cancel actions
    setupCancelForDialog("usernotloggedin", "usernotloggedinclose");
    setupCancelForDialog("filetoolarge", "filetoolargeclose");
    setupCancelForDialog("transcriptionfailed", "transcriptionfailedclose");
    setupCancelForDialog("notenoughcredits", "notenoughcreditsclose");
    setupCancelForDialog("httpfailure", "httpfailureclose");

    // Get ID token
    let gotToken = await getIDToken();
    if (requireIDTokenFeature == true && gotToken == false) {
      console.log("User has not logged-in. Exiting");
      const msg = "To use Saras, please sign in and restart Word. Error: " + userTokenError;
      showModalDialogCustomMessage("usernotloggedin", msg);
      return;
    }

    await transcribe();
    updateUsageInformation();

    document.getElementById(StartRecordingButtonName).onclick = startrecording;
    document.getElementById(StopRecordingButtonName).onclick = stoprecording;
    document.getElementById(StopRecordingButtonName).setAttribute(DisabledAttributeName, "");
    document.getElementById(UploadRecordingButtonName).onclick = uploadaudio;
  }
});

// Start recording if button is enabled
Office.actions.associate("StartRecording", () => {
  if (document.getElementById(StartRecordingButtonName).getAttribute(DisabledAttributeName) == null) {
    startrecording();
  } else {
    console.log("Start Recording keyboard shortcut but button is disabled");
  }
});

// Stop recording if button is enabled
Office.actions.associate("StopRecording", () => {
  if (document.getElementById(StopRecordingButtonName).getAttribute(DisabledAttributeName) == null) {
    stoprecording();
  } else {
    console.log("Stop Recording keyboard shortcut but button is disabled");
  }
});

// Upload recording if button is enabled
Office.actions.associate("UploadRecording", () => {
  if (document.getElementById(UploadRecordingButtonName).getAttribute(DisabledAttributeName) == null) {
    uploadaudio();
  } else {
    console.log("Upload Recording keyboard shortcut but button is disabled");
  }
});

// Close dialog
Office.actions.associate("CancelDialog", () => {
  if (document.getElementById("transcriptionfailed").getAttribute("open") != null) {
    console.log("transcriptionfailed dialog open. Closing");
    const transcriptionCloseButton = document.getElementById("transcriptionfailedclose");
    transcriptionCloseButton.click();
  } else if (document.getElementById("filetoolarge").getAttribute("open") != null) {
    console.log("filetoolarge dialog open. Closing");
    const fileCloseButton = document.getElementById("filetoolargeclose");
    fileCloseButton.click();
  } else if (document.getElementById("usernotloggedin").getAttribute("open") != null) {
    console.log("usernotloggedin dialog open. Closing");
    const userCloseButton = document.getElementById("usernotloggedinclose");
    userCloseButton.click();
  } else if (document.getElementById("notenoughcredits").getAttribute("open") != null) {
    console.log("notenoughcredits dialog open. Closing");
    const creditsCloseButton = document.getElementById("notenoughcreditsclose");
    creditsCloseButton.click();
  } else if (document.getElementById("httpfailure").getAttribute("open") != null) {
    console.log("httpfailure dialog open. Closing");
    const httpCloseButton = document.getElementById("httpfailureclose");
    httpCloseButton.click();
  } else {
    console.log("No dialog open to close");
  }
});

function showModalDialog(elementId: string) {
  const dialog = document.getElementById(elementId) as HTMLDialogElement;
  dialog.showModal();
}

function showModalDialogCustomMessage(elementId: string, message: string) {
  const dialog = document.getElementById(elementId) as HTMLDialogElement;
  const messageElement = dialog.querySelector('p');
  messageElement.textContent = message; // Set the message dynamically
  dialog.showModal();
}

// If user clicks on "Close" button, close the dialog
// User can also press Esc to dismiss the dialog
function setupCancelForDialog(dialogId: string, buttonId: string) {
  const dialogElement = document.getElementById(dialogId) as HTMLDialogElement;
  const closeButton = document.getElementById(buttonId);
  closeButton.addEventListener("click", () => {
    dialogElement.close();
  });
}

function requestPermissionsOfficeOnline() {
  // Request permission from a user to access their microphone.
  if (Office.context.platform === Office.PlatformType.OfficeOnline) {
    const deviceCapabilities = [Office.DevicePermissionType.microphone];
    Office.devicePermission
      .requestPermissions(deviceCapabilities)
      .then((isGranted) => {
        if (isGranted) {
          console.log("Permission granted.");
          // Reload your add-in before you run code that uses the device capabilities.
          location.reload();
        } else {
          console.log("Permission has been previously granted and is already set in the iframe.");
          // Since permission has been previously granted, you don't need to reload your add-in.
        }
      })
      .catch((error) => {
        console.error("Permission denied.", error);
        // For now, letting getUserMedia() to fail and let error be reported through it
        // for simplicity (across Desktop and Web scenarios)
      });
  }
}

export async function startrecording() {
  uistartrecording();
  requestPermissionsOfficeOnline();

  navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => {
      // check if above mime type is supported in browser and instantiate recorder
      let mimeType: string = "";
      // Using forEach method
      mimeToExtensionMap.forEach((value, key) => {
        console.log(`Is ${key} supported? ${MediaRecorder.isTypeSupported(key) ? "Maybe!" : "Nope :("}`);
        if (mimeType == "" && MediaRecorder.isTypeSupported(key)) {
          mimeType = key;
        }
      });

      if (mimeType == "") {
        console.log("Using default mimetype supported by browser");
        mediaRecorder = new MediaRecorder(stream);
      } else {
        mediaRecorder = new MediaRecorder(stream, { mimeType });
      }
      mediaRecorder.start();

      mediaRecorder.addEventListener("dataavailable", (event) => {
        chunks.push(event.data);
      });
    })
    .catch((error) => {
      console.error("Error accessing microphone:", error);
      document.getElementById(StartRecordingButtonName).textContent =
        "Unable to access microphone. Please allow access and click here again";
      document.getElementById(StartRecordingButtonName).removeAttribute(DisabledAttributeName);
      document.getElementById(StopRecordingButtonName).setAttribute(DisabledAttributeName, "");
    });
}

function stoprecording() {
  return Word.run(async (context) => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      mediaRecorder.addEventListener("stop", async () => {
        // Create a Date object for the current time
        const now = new Date();
        const year = now.getFullYear().toString();
        const month = (now.getMonth() + 1).toString(); //0-based
        const date = now.getDate().toString();
        const hour = now.getHours().toString();
        const min = now.getMinutes().toString();
        const secs = now.getSeconds().toString();
        const filenameprefix = "Saras-Word-";
        const delimiter = "-";
        const basefilename = filenameprefix.concat(
          year,
          delimiter,
          month,
          delimiter,
          date,
          delimiter,
          hour,
          delimiter,
          min,
          delimiter,
          secs
        );
        let fileExtension = ".unk";
        console.log(mediaRecorder.mimeType);
        if (mimeToExtensionMap.has(mediaRecorder.mimeType)) {
          fileExtension = mimeToExtensionMap.get(mediaRecorder.mimeType);
        }
        let filename = basefilename.concat(fileExtension);
        console.log(filename);
        // create a File object. Be sure to set mimetype as it'll
        // be used to download recordings locally as well as in
        // HTTP request to transcribe the recording
        recording = new File(chunks, filename, { type: mediaRecorder.mimeType });

        uitranscribinginprogress(false);
        await kickofftranscribe(context);

        if (getradiobutton("save-recording") == "yes") {
          console.log("saving recording");
          saveAs(recording);
        }

        chunks = [];
        uireadytorecord();
      });
    }
  });
}

function uploadaudio() {
  return Word.run(async (context) => {
    console.log("uploadaudio");

    // Create an input element and allow
    // user to select a file
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "audio/*,video/*";

    // Trigger the click event to open the file dialog
    input.click();

    // Handle the selected file
    input.onchange = async () => {
      if (input.files[0].size > maxaudiosizebytes) {
        console.log("File size is too big");
        showModalDialog("filetoolarge");
        return;
      }
      recording = input.files[0];
      uitranscribinginprogress(true);
      await kickofftranscribe(context);
      uireadytorecord();
    };
  });
}

function uistartrecording() {
  console.log("startrecording");
  document.getElementById(StartRecordingButtonName).textContent = "Recording... limit to 25 minutes";
  document.getElementById(StartRecordingButtonName).setAttribute(DisabledAttributeName, "");
  document.getElementById(StopRecordingButtonName).removeAttribute(DisabledAttributeName);
  document.getElementById(UploadRecordingButtonName).setAttribute(DisabledAttributeName, "");
}

function uireadytorecord() {
  console.log("readytorecord");
  document.getElementById(StartRecordingButtonName).textContent = "Start Recording";
  document.getElementById(StartRecordingButtonName).removeAttribute(DisabledAttributeName);
  document.getElementById(StopRecordingButtonName).textContent = "Stop Recording";
  document.getElementById(StopRecordingButtonName).setAttribute(DisabledAttributeName, "");
  document.getElementById(UploadRecordingButtonName).textContent = "Upload Recording";
  document.getElementById(UploadRecordingButtonName).removeAttribute(DisabledAttributeName);
}

function uitranscribinginprogress(usingUploadedAudio) {
  console.log("transcribinginprogress");
  document.getElementById(StartRecordingButtonName).setAttribute(DisabledAttributeName, "");
  document.getElementById(StopRecordingButtonName).setAttribute(DisabledAttributeName, "");
  if (usingUploadedAudio == true) {
    document.getElementById(UploadRecordingButtonName).textContent = "Transcribing";
  } else {
    document.getElementById(StopRecordingButtonName).textContent = "Transcribing";
  }
  document.getElementById(UploadRecordingButtonName).setAttribute(DisabledAttributeName, "");
}

function updateUsageInformation() {
  if (usedMinutes != undefined && usedMinutes >= 0) {
    document.getElementById("usage").style.display = "block";

    // Calculate available minutes. If it is negative, set it to 0
    // for UX reasons 
    let minutesAvailable: number = creditsMinutes - usedMinutes;
    if (minutesAvailable < 0) {
      minutesAvailable = 0;
    }
    let sentence: string = undefined;
    sentence = "Minutes used: " + usedMinutes.toFixed() + "<br>";
    sentence += "Credits (free and paid): " + creditsMinutes.toFixed() + "<br>";
    sentence += "Minutes available: " + minutesAvailable.toFixed() + "<br>";
    document.getElementById("usage").innerHTML = sentence;
    if (minutesAvailable <= thresholdForAvailableMinutesWarning) {
      // Create a new anchor element
      const link = document.createElement("a");
      // Set the href attribute
      link.href = paymentlink + "?client-reference-id=" + getOIDFromToken();
      // Set the text content
      link.textContent = "Click here to pay for more minutes";
      // Optionally, set additional attributes (like target)
      link.target = "_blank"; // Opens the link in a new tab
      // Append the anchor element to the desired parent element in the DOM
      const parentElement = document.getElementById("usage");
      if (parentElement) {
        parentElement.appendChild(link);
      }
    }
  } else {
    document.getElementById("usage").style.display = "none";
  }
}

async function kickofftranscribe(context) {
  console.log("kickofftranscribe");
  await transcribe();

  // Insert text at the cursor.
  // Make sure cursor is after inserted text.
  // Past customer feedback:
  //  1. Insert at cursor (not at the end)
  //  2. Move cursor after inserted text (otherwise, people have to
  //    manually move cursor after the last inserted text, for the next
  //    recording/transcription), like Dictate
  //  3. Insert text (and not para), like Dictate
  //  4. Insert whitespace at the end, like Dictate
  if (transcription != undefined) {
    // Insert at cursor
    let transcriptRange = context.document.getSelection().insertText(transcription + " ", Word.InsertLocation.end);
    // Format text
    transcriptRange = updateFormatting(transcriptRange);
    // Move cursor to immediately after inserted text
    transcriptRange.select(Word.SelectionMode.end);
    await context.sync();
    transcription = undefined;
  } else if (hasFormattingChanged() == true) {
    // No changes to UI (as formatting change will be applied
    // when user provides actual text to be inserted into the
    // Word document)
  } else if (creditsMinutes != undefined && usedMinutes != undefined && creditsMinutes - usedMinutes <= 0) {
    // There is a side-effect of having "notenoughcredits" condition ahead of
    // "transcriptionfailed". Even if the server couldnt be contacted,
    // "notenoughcredits" dialog will be shown as long as we were able
    // to reach the server earlier and determine that there are no credits
    console.log("notenoughcredits");
    showModalDialog("notenoughcredits");
  } else if (failureReason == FailureReasons.HTTPFailed) {
    showModalDialog("httpfailure");
  } else {
    // This is a catch-all for all other failures
    console.log("transcription is undefined");
    showModalDialog("transcriptionfailed");
  }
  updateUsageInformation();
}

// Define an function that makes the request and handles the response
async function transcribe() {
  // Create a new FormData object
  const formData = new FormData();

  // Bump version every time parameters are updated
  // This would make it easier for backend to decipher
  // what's expected vs not
  formData.set("version", "1");

  // Append the file to the FormData object with the key "recording"
  // Recording is undefined when add-in loads. This allows us to
  // query for usage information without actually attempting a
  // transcribe operation
  if (recording != undefined) {
    formData.set("recording", recording);
  }

  // Language to be used in transcription
  // Same - Transcribe in same language as input
  // English - Transcribe in English irrespective of input language
  formData.set("outputlanguage", getradiobutton("transcribe-same-language"));

  // Refresh token if necessary
  await checkAndRefreshToken();
  formData.set("authorization", userTokenEncoded);

  const url = "https://pranak-transcribe.azurewebsites.net/api/HttpTrigger";
  const method = "POST";

  // Use fetch to send the POST request to the server
  try {
    failureReason = FailureReasons.Success;
    // use await to get the response from fetch
    const response = await fetch(url, {
      method: method,
      body: formData,
    });

    if (response.ok) {
      console.log("File uploaded and transcribed successfully");
      const resp = await response.json();
      transcription = doPostProcessing(resp["transcription"]);
      if (resp["duration"] != undefined && resp["duration"] >= 0) {
        usedMinutes = resp["duration"] / 60;
        creditsMinutes = resp["credits"];
      }
    } else {
      console.log(response.status);
      console.log(response.statusText);
      console.log("File upload failed");
      failureReason = FailureReasons.GenericFailure;
    }
  } catch (error) {
    // Handle any errors that may occur
    console.error("inside catch");
    console.error(error);
    failureReason = FailureReasons.HTTPFailed;
  }
}

function getradiobutton(name) {
  const radioButtons = document.getElementsByName(name);
  let selectedValue: string;
  for (let i = 0; i < radioButtons.length; i++) {
    if ((radioButtons[i] as HTMLInputElement).checked) {
      selectedValue = (radioButtons[i] as HTMLInputElement).value;
      break;
    }
  }
  return selectedValue;
}

async function checkAndRefreshToken() {
  console.log("checkAndRefreshToken called");
  // If token wasn't initialized, skip refreshing it
  // for we don't want to prompt user in such cases
  if (userTokenEncoded == null || tokenExpiryTime == null) {
    return;
  }

  // If token expiry time is in the future, there is
  // no need to refresh token
  const timeInSeconds = Date.now() / 1000;
  if (tokenExpiryTime >= timeInSeconds) {
    return;
  }

  // Refresh token
  getIDToken();
}

async function getIDToken() {
  try {
    console.log("getting token");
    userTokenEncoded = await OfficeRuntime.auth.getAccessToken({
      allowConsentPrompt: true,
      allowSignInPrompt: true,
    });
    let userToken: JwtPayload = jwtDecode<JwtPayload>(userTokenEncoded);
    tokenExpiryTime = userToken.exp;
  } catch (error) {
    console.log("SSO failed, falling back to MSAL:", error.code);
    userTokenError = error.code;
  }
  return userTokenEncoded != null;
}

function getOIDFromToken() {
  if (userTokenEncoded == null) {
    return null;
  }
  let userToken: JwtPayload = jwtDecode<JwtPayload>(userTokenEncoded);
  return userToken.oid;
}